import { useState } from 'preact/hooks'
import { Loader2, ShieldCheck, ChevronDown, ChevronRight, AlertCircle } from 'lucide-preact'
import type { AssetQaFindingDto, AssetQaReferenceGapDto, AssetQaRunViewDto } from '@/types/assetQa'
import type { WebTabKey } from '@/hooks/useWebTabRouter'
import { resolveReadinessTab } from '@/constants/signOffReadiness'
import { QA_VERDICT_META, QA_CHECK_STATUS_META } from '../constants/assetQaMeta'
import { AssetQaFixList } from './AssetQaFixList'
import { groupChecks, describeGroup, type AssetQaGroup } from './assetQaGrouping'
import { AssetQaChecklist } from './AssetQaChecklist'
import { AssetQaOverrideModal } from './AssetQaOverrideModal'

/**
 * A reference gap as it might arrive over the wire, in either shape it has ever had.
 *
 * ⚑⚑ `ReferenceGaps` was `string[]` until ORB-889 and is now a list of objects carrying the
 * sign-off gate's field name.
 *
 * This is NOT for the deploy window raised on PR #144. An already-open tab is running the previous
 * bundle, and nothing shipped here can change what that code does — that direction is unfixable
 * from this side. It is for the one `docs/PROD-PROMOTION.md` warns about: the API has to reach an
 * environment before the dashboard that calls it, so a new bundle can meet an older API still
 * returning bare strings, and this amber block is the surface that would break.
 *
 * Deliberately temporary, and cheap to remove: once the API is deployed everywhere the dashboard
 * is, this type and its two readers go, and the generated type already says objects.
 */
type WireReferenceGap = AssetQaReferenceGapDto | string

const gapMessage = (gap: WireReferenceGap): string =>
    typeof gap === 'string' ? gap : gap.Message

/** A legacy string gap carries no destination — the same state as a gap no field can fix. */
const gapFieldName = (gap: WireReferenceGap): string | null | undefined =>
    typeof gap === 'string' ? null : gap.FieldName

interface Props {
    run: AssetQaRunViewDto | null
    loading: boolean
    /** Null when the version predates QA, or the type is not checked — not an error. */
    notChecked: boolean
    /** Set when the read itself failed, which is NOT the same as never having been checked. */
    loadError: string | null
    canOverride: boolean
    /**
     * Dismisses one finding, or every open finding in a folded group when `groupKey` is given
     * (ORB-967). The reason is recorded against each member individually either way — the group is
     * how a person says it once, not how it is stored.
     */
    onOverride: (checkId: string, code: string, reason: string, groupKey?: string) => Promise<void>
    /**
     * Takes the reader to the tab that owns a gap's field, closing the overlay on the way
     * (ORB-889). Optional: the panel renders the gap as plain text without it, which is what
     * happens wherever this is drawn outside the web.
     */
    onGoToWebTab?: (tab: WebTabKey) => void
}

/**
 * The QA result for the version under review.
 *
 * Laid out in the order the rulebook's report format fixes: the verdict, then the fix list, then
 * the complete checklist grid, then what a human still has to check. No commentary sections — the
 * shape is what makes the report quick to act on.
 */
export function AssetQaPanel({ run, loading, notChecked, loadError, canOverride, onOverride, onGoToWebTab }: Props) {
    const [showChecklist, setShowChecklist] = useState(false)
    // The whole group, not one finding: a folded row's Override dismisses every open member, and
    // the modal needs the count to say so before anyone commits to it.
    const [overrideTarget, setOverrideTarget] = useState<AssetQaGroup<AssetQaFindingDto> | null>(null)
    // Keyed by findingKey, not CheckId: findings on a run recorded before CheckId existed all
    // carry an empty one, so keying on it marked every legacy row as saving at once.
    const [overridingKey, setOverridingKey] = useState<string | null>(null)
    const [overrideError, setOverrideError] = useState<string | null>(null)

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Checking…
            </div>
        )
    }

    // A failed read is not an unchecked version. Collapsing the two would tell a reviewer the
    // asset was never checked when in fact the result exists and could not be fetched — which is
    // the more alarming of the two states, reported as the more reassuring one.
    if (loadError) {
        return (
            <p className="flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                The quality check could not be loaded. {loadError}
            </p>
        )
    }

    // A version with no run is an ordinary state, not a failure: QA runs asynchronously after an
    // upload, and anything uploaded before the checkpoint shipped will never have one. Saying so
    // is better than an empty panel that reads as a clean result.
    if (notChecked || !run) {
        return (
            <p className="text-xs text-slate-500 dark:text-slate-400">
                This version has not been checked.
            </p>
        )
    }

    const verdict = QA_VERDICT_META[run.Verdict]
        ?? { label: run.Verdict, classes: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300', tone: 'warn' as const }

    // Only Open counts as owed. Fixed and False Positive are resolved too, and treating
    // "not overridden" as "outstanding" would make the count disagree with the verdict beside it.
    // ⚑ The length here and `countOpenFindings` must always agree, which is why the header above
    // this panel asks that helper rather than counting again (ORB-987).
    const openFindings = run.Findings.filter(finding => finding.Status === 'Open')
    const needsHuman = run.Checks.filter(check => check.Status === 'Needs Human Verification')

    const handleOverride = async (reason: string) => {
        if (!overrideTarget) return
        setOverridingKey(overrideTarget.key)
        setOverrideError(null)
        try {
            // ⚑ The group key only goes up when the row actually folded more than one finding.
            // Sending it for a row of one would ask the server to match on a key where the check id
            // already identifies the finding exactly, which is a wider net for no gain.
            await onOverride(
                overrideTarget.lead.CheckId,
                overrideTarget.lead.Code,
                reason,
                overrideTarget.members.length > 1 ? overrideTarget.key : undefined
            )
            setOverrideTarget(null)
        } catch (error) {
            // Caught rather than left to reject: the modal stays open with the reason still typed,
            // and the person is told why. Without this the button simply re-enables and the
            // dismissal appears to have been ignored.
            setOverrideError(error instanceof Error ? error.message : 'The override could not be saved.')
        } finally {
            setOverridingKey(null)
        }
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${verdict.classes}`}>
                    {verdict.label}
                </span>
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    {openFindings.length === 0
                        ? 'No open findings'
                        : `${openFindings.length} to fix`}
                </span>
            </div>

            {/* ⚑ What the panel is FOR, in one line (ORB-987). The team read this surface as the
                platform rejecting their work, and both halves of this sentence are the correction:
                the checks are meant to over-report, and an override is a record rather than a
                dismissal — Hyrzl's words on the 1 Oct call were "we flag this, but at your
                discretion, this is allowable."

                ⚑ Above the reference-gap callout and the findings, below the verdict. The verdict is
                still the headline; this says how to act on what follows, so it has to come before the
                first warning rather than after it.

                ⚑ Deliberately not a callout, an icon or a card. The panel already carries an amber
                box and a fix list, and a third framed block would read as a fourth finding. Plain
                muted text in the style the rest of the panel already uses. */}
            {/* ⚑ The second half changes for a reader who cannot override (ORB-987). AssetQaFixList
                hides the control from anyone without the workspace edit role, so telling them to
                override would point at a button that is not there — and a reviewer who holds no edit
                role is exactly the person who needs to know who can. Raised by Copilot on PR 244. */}
            <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                {canOverride
                    ? 'Checks are deliberately sensitive. Override anything that is right for this client — the reason is kept.'
                    : 'Checks are deliberately sensitive. Anything that is right for this client can be overridden by someone with edit access, and the reason is kept.'}
            </p>

            {/* What the run could not see. Shown above a clean result on purpose: "no findings" and
                "no findings, but the brand palette was never available" are different claims. */}
            {run.ReferenceGaps.length > 0 && (
                <div className="rounded-md border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20 p-2.5">
                    {/* ⚑ "some of this web's details" rather than "part of the reference"
                        (ORB-889). AssetQaReference is a class name and had no business in a
                        sentence a customer reads. It also pointed nowhere: the reader's next action
                        is on the web, not on the asset filling the screen in front of them. */}
                    <p className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-800 dark:text-amber-300">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Checked without some of this web's details
                    </p>
                    <ul className="mt-1 space-y-0.5">
                        {(run.ReferenceGaps as WireReferenceGap[]).map(gap => {
                            // ⚑⚑ The same allowlist the sign-off popover routes on, deliberately.
                            // The gap carries the gate's own field name, so one absence gets one
                            // destination rather than two surfaces each guessing at their own.
                            const message = gapMessage(gap)
                            const tab = resolveReadinessTab(gapFieldName(gap))
                            return (
                                <li key={message} className="text-[11px] text-amber-700 dark:text-amber-400 break-words">
                                    {message}
                                    {tab && onGoToWebTab && (
                                        <button
                                            type="button"
                                            onClick={() => onGoToWebTab(tab)}
                                            className="ml-1.5 inline-flex items-center gap-0.5 font-medium underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-200 focus:outline-none focus:ring-1 focus:ring-amber-500 rounded"
                                        >
                                            Fix on the web
                                            <ChevronRight className="w-3 h-3" />
                                        </button>
                                    )}
                                </li>
                            )
                        })}
                    </ul>
                </div>
            )}

            {run.Findings.length > 0 ? (
                <AssetQaFixList
                    findings={run.Findings}
                    canOverride={canOverride}
                    onOverride={group => setOverrideTarget(group)}
                    overridingKey={overridingKey}
                />
            ) : (
                <p className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                    <ShieldCheck className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                    Nothing was flagged on this version.
                </p>
            )}

            {/* Its own section, as the report format requires — not folded into the grid below,
                where "could not check" would sit among the passes and read like one. */}
            {needsHuman.length > 0 && (
                <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2.5">
                    {/* ⚑ The status's own label, read from QA_CHECK_STATUS_META rather than typed
                        again (ORB-987). This heading and the per-check rows below name the same
                        state, and they had been free to drift — the heading carried our invented
                        "Needs a human to check" while the matrix calls it "Needs human verification".
                        The count stays: it says how many rows follow, which is not a badge of work
                        owed. */}
                    <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">
                        {QA_CHECK_STATUS_META['Needs Human Verification'].label} ({needsHuman.length})
                    </p>
                    {/* ⚑ Folded like the fix list and the grid (ORB-967). This is the list that
                        hurt most on the reported asset: 39 of its 41 rows were one near-miss band
                        of one palette role, and unlike a finding none of them can be dismissed —
                        there is nothing to click. Thirty-nine unclearable rows say no more than one
                        does, and they bury the two rows that are about something else. */}
                    <ul className="mt-1 space-y-1">
                        {groupChecks(needsHuman).map(folded => {
                            const summary = describeGroup(folded)
                            return (
                                <li key={folded.key} className="text-[11px] text-slate-600 dark:text-slate-300 break-words">
                                    {folded.lead.Description}
                                    {summary
                                        ? (
                                            <span className="text-slate-500 dark:text-slate-400">
                                                {' — '}{summary}
                                                {folded.isGradient
                                                    ? '; please check by eye that they are on brand'
                                                    : '; check by eye'}
                                            </span>
                                        )
                                        : folded.lead.Reason && (
                                            <span className="text-slate-500 dark:text-slate-400"> — {folded.lead.Reason}</span>
                                        )}

                                    {/* ⚑⚑ The fold must not cost the detail (ORB-980). A collapsed
                                        "2 gradients" says how many but not WHICH, and the thing a person
                                        is being asked to look at is a specific ramp — its two ends, how
                                        much of the asset it covers and how far those ends sit from the
                                        palette. That is what makes the row actionable by eye rather than
                                        just a notification, so each member keeps its own sentence one
                                        click away. Same shape as the fix list's own "Show all". */}
                                    {folded.members.length > 1 && (
                                        <details className="mt-1">
                                            <summary className="cursor-pointer text-[11px] text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
                                                Show all {folded.members.length}
                                            </summary>
                                            <ul className="mt-1 ml-3 space-y-1 list-disc list-outside marker:text-slate-400 dark:marker:text-slate-500">
                                                {folded.members.map(member => (
                                                    <li key={member.Id} className="text-[11px] text-slate-500 dark:text-slate-400 break-words">
                                                        {member.Reason}
                                                    </li>
                                                ))}
                                            </ul>
                                        </details>
                                    )}
                                </li>
                            )
                        })}
                    </ul>
                </div>
            )}

            <div>
                <button
                    type="button"
                    onClick={() => setShowChecklist(value => !value)}
                    aria-expanded={showChecklist}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                >
                    {showChecklist
                        ? <ChevronDown className="w-3.5 h-3.5" />
                        : <ChevronRight className="w-3.5 h-3.5" />}
                    {showChecklist ? 'Hide' : 'Show'} everything that was checked ({run.Checks.length})
                </button>

                {showChecklist && (
                    <div className="mt-2">
                        <AssetQaChecklist checks={run.Checks} />
                    </div>
                )}
            </div>

            {overrideTarget && (
                <AssetQaOverrideModal
                    finding={overrideTarget.lead}
                    covers={overrideTarget.members.filter(member => member.Status === 'Open').length}
                    saving={overridingKey !== null}
                    error={overrideError}
                    onCancel={() => { setOverrideTarget(null); setOverrideError(null) }}
                    onConfirm={handleOverride}
                />
            )}
        </div>
    )
}
